/**
 * What the free tier's card says, given who is looking at it.
 *
 * It is the bottom rung of the ladder now, so unlike the old band it is
 * always drawn — a card that comes and goes would reflow a four-column grid
 * on every page load. What changes is the action area: an offer, a statement
 * of what is left, or nothing at all.
 *
 *  - a visitor with no account is offered one, because it costs nothing;
 *  - a registered account without a subscription is looking at its own plan,
 *    so the card says how much of it is left and when it comes back;
 *  - a subscriber is paying for a bigger rung and needs no button on this
 *    one — but still sees it, because "$0 → $2 → $4 → $9.99" is the ladder
 *    they bought into and hiding its first step breaks the comparison.
 *
 * The fourth case is the one worth a function: **not knowing yet**. Auth0
 * restores the session asynchronously and the profile that says whether this
 * account subscribes arrives later still, so for the first moments a
 * subscriber is indistinguishable from an anonymous visitor. Guessing offers
 * a paying customer a free account and then takes the offer away; waiting
 * costs one beat, below the fold, in a card that is drawn either way.
 */
export type FreePlanTierState = 'unknown' | 'register' | 'current' | 'subscribed'

export function freePlanTierState(v: {
  /** Auth0 is still restoring the session */
  authLoading: boolean
  isAuthenticated: boolean
  /** The account profile (credits, subscription) hasn't answered yet */
  profileLoading: boolean
  isSubscribed: boolean
}): FreePlanTierState {
  if (v.authLoading) return 'unknown'
  if (!v.isAuthenticated) return 'register'
  if (v.profileLoading) return 'unknown'
  return v.isSubscribed ? 'subscribed' : 'current'
}
